import { create } from 'zustand'
import { POI } from '../config/cameraConfig'
import { poiService } from '../services/poiService'
import type { Camera } from 'three'
import type { OrbitControls } from 'three-stdlib'

interface CameraState {
  // POI data
  pois: POI[]
  isLoadingPOIs: boolean
  poisError: string | null

  // Current POI
  currentPOI: POI | null
  setCurrentPOI: (poi: POI | null) => void

  // Camera mode
  cameraMode: 'orbit' | 'cinematic' | 'transitioning'
  setCameraMode: (mode: 'orbit' | 'cinematic' | 'transitioning') => void

  // POI panel visibility
  showPOIPanel: boolean
  setShowPOIPanel: (show: boolean) => void

  // Selected building for info display
  selectedBuilding: string | null
  setSelectedBuilding: (building: string | null) => void

  // Camera constraints override (for specific POIs)
  constraintsOverride: Partial<OrbitControls> | null
  setConstraintsOverride: (constraints: Partial<OrbitControls> | null) => void

  // Camera and controls refs (set by CameraController)
  cameraRef: Camera | null
  controlsRef: OrbitControls | null
  setCameraRef: (camera: Camera | null) => void
  setControlsRef: (controls: OrbitControls | null) => void

  // POI editing mode (for transform controls)
  editingPOIId: string | null
  setEditingPOIId: (id: string | null) => void

  // Temporary POI position during drag (for live preview)
  tempPOIPosition: { id: string; target: { x: number; y: number; z: number } } | null
  setTempPOIPosition: (
    data: { id: string; target: { x: number; y: number; z: number } } | null
  ) => void

  // Transition trigger (increment to re-trigger camera transition to same POI)
  transitionTrigger: number
  incrementTransitionTrigger: () => void

  // Actions for POI management
  loadPOIs: () => Promise<void>
  updatePOI: (id: string, data: Partial<POI>) => Promise<void>
  refreshPOIs: () => Promise<void>
}

export const useCameraStore = create<CameraState>((set, get) => ({
  // Initial state
  pois: [],
  isLoadingPOIs: false,
  poisError: null,
  currentPOI: null,
  cameraMode: 'orbit',
  showPOIPanel: true,
  selectedBuilding: null,
  constraintsOverride: null,
  cameraRef: null,
  controlsRef: null,
  editingPOIId: null,
  tempPOIPosition: null,
  transitionTrigger: 0,

  // Actions
  setCurrentPOI: (poi) => set({ currentPOI: poi }),
  setCameraMode: (mode) => set({ cameraMode: mode }),
  setShowPOIPanel: (show) => set({ showPOIPanel: show }),
  setSelectedBuilding: (building) => set({ selectedBuilding: building }),
  setConstraintsOverride: (constraints) => set({ constraintsOverride: constraints }),
  setCameraRef: (camera) => set({ cameraRef: camera }),
  setControlsRef: (controls) => set({ controlsRef: controls }),
  setEditingPOIId: (id) => set({ editingPOIId: id }),
  setTempPOIPosition: (data) => set({ tempPOIPosition: data }),
  incrementTransitionTrigger: () =>
    set((state) => ({ transitionTrigger: state.transitionTrigger + 1 })),

  // Load POIs from backend
  loadPOIs: async () => {
    set({ isLoadingPOIs: true, poisError: null })
    try {
      const pois = await poiService.fetchPOIs()
      set({ pois, isLoadingPOIs: false })
    } catch (error) {
      set({
        poisError: error instanceof Error ? error.message : 'Failed to load POIs',
        isLoadingPOIs: false,
      })
    }
  },

  // Update a POI and refresh the list
  updatePOI: async (id: string, data: Partial<POI>) => {
    try {
      await poiService.updatePOI(id, data)
      // Refresh POIs to get updated data
      await get().loadPOIs()
    } catch (error) {
      console.error('Failed to update POI:', error)
      throw error
    }
  },

  // Refresh POIs (alias for loadPOIs for clarity)
  refreshPOIs: async () => {
    await get().loadPOIs()
  },
}))
